import { Flex, Pill, ProviderConsumer, Text } from '@fluentui/react-northstar';
import _ from 'lodash';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import ReactDOM from 'react-dom';
import CloseIcon from '../../../../svg/close.svg';
import DownArrow from '../../../../svg/dropdown-downarrow.svg';
import { getDropdownBoxShadow } from '../../../tikit/ticketHelper';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { getBackgroundColor } from '../TicketForm';
import './NestedDropdown.css';
import Styles from './NestedDropdown.module.css';

interface Props {
  label?: string;
  placeholder?: string;
  items: Item[];
  dataItem?: Item;
  defaultValue: string;
  onChange: (item: Item) => void;
  disabled?: boolean;
  showClearBtn?: boolean;
  error?: boolean;
  tabIndex?: number;
  isMulti?: boolean;
  onMultiChange?: (items: number[]) => void;
  isRequired?: boolean;
  triggerDefaultValueSet? : boolean;
}

export interface Item {
  id: number;
  parentId: null | number;
  position: number;
  value: string;
}

interface TreeItemProps {
  id: string;
  optionId: string;
  value: string;
  onSelectCallback: () => void;
  isActiveValue: boolean;
  isDisabledValue: boolean;
  isHighlighted: boolean;
  paddingLeft: string;
}

const TreeItem = ({ optionId, onSelectCallback, value, isActiveValue, isDisabledValue, isHighlighted, paddingLeft }: TreeItemProps) => {
  const styleClass = `${Styles.ListItemActive} font-semibold selected-value`;
  return (
    <div
      id={optionId}
      role="option"
      aria-selected={isActiveValue}
      aria-disabled={isDisabledValue}
      style={{ paddingLeft: paddingLeft }}
      className={`${isDisabledValue ? Styles.ListItemDisabled : Styles.ListItem} ${isHighlighted ? Styles.ListItemHighlighted : ''} flex items-center py-1.5 pr-4 ${isDisabledValue ? '' : 'cursor-pointer'}`}
      onClick={() => isDisabledValue ? {} : onSelectCallback()}
    >
      <div className={`${isActiveValue && styleClass}`}>
        {value}
      </div>
    </div>
  );
};

const RecursiveTree = ({ data, onSelectCallback, activeValue, isMulti, activeValueIds, highlightedItemId, getOptionId }) => {
  const createTree = (item: any, padding: number) => {
    padding++;
    return item && <>
      <TreeItem
        id={item.id}
        key={item.id}
        optionId={getOptionId(item.id)}
        onSelectCallback={() => onSelectCallback(item)}
        value={item.value}
        isActiveValue={!isMulti ? item.value === activeValue : false}
        isDisabledValue={isMulti ? activeValueIds.some(av => av == item.id) : false}
        isHighlighted={item.id === highlightedItemId}
        paddingLeft={`${padding > 1 ? (padding * 0.75) : padding}rem`}
      />
      {item.children && item.children.map((item: any) => <Fragment key={item.id}>{createTree(item, padding)}</Fragment>)}
    </>;
  }

  return (
    <div>
      {data.map((item: any, i: any) => (
        <Fragment key={i}>{createTree(item, 0)}</Fragment>
      ))}
    </div>
  );
};

const flattenTree = (nodes: any[]): any[] =>
  nodes.reduce((acc, node) => {
    acc.push(node);
    if (node.children?.length) acc.push(...flattenTree(node.children));
    return acc;
  }, []);

export const NestedDropdown = ({
  label,
  items = [],
  dataItem,
  placeholder,
  onChange,
  defaultValue,
  disabled,
  showClearBtn,
  error,
  tabIndex,
  isMulti,
  onMultiChange,
  isRequired,
  triggerDefaultValueSet
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const instanceIdRef = useRef(_.uniqueId('nested-dropdown-'));
  const listboxId = `${instanceIdRef.current}-listbox`;
  const getOptionId = useCallback((id: number | string) => `${instanceIdRef.current}-option-${id}`, []);
  const [activeValue, setActiveValue] = useState('');
  const [activeValueArray, setActiveValueArray] = useState([]);

  const [activeValuesIds, setActiveValuesIds] = useState<number[]>([]);
  const [activeValuesArray, setActiveValuesArray] = useState<Record<string, string[]>>({});
  const [show, setShow] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showAbove, setShowAbove] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });

  const showRef = useRef(show);
  useEffect(() => {
    showRef.current = show;
  }, [show]);

  const DROPDOWN_MAX_HEIGHT = 300; // should match your OptionContainer max-height in CSS
  const calculateMenuPosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      const shouldShowAbove = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;
      setShowAbove(shouldShowAbove);

      setMenuPosition({
        top: shouldShowAbove ? rect.top - DROPDOWN_MAX_HEIGHT : rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, []);

  const handleToggle = () => {
    if (!show) calculateMenuPosition();
    setShow(prev => !prev);
  };

  useEffect(() => {
    if (show) {
      window.addEventListener('scroll', calculateMenuPosition, true);
      window.addEventListener('resize', calculateMenuPosition);
      return () => {
        window.removeEventListener('scroll', calculateMenuPosition, true);
        window.removeEventListener('resize', calculateMenuPosition);
      };
    }
  }, [show, calculateMenuPosition]);

  /**
   * Open on the selected item rather than at the top of the list.
   *
   * Inert for the short lists this component was written for - nothing overflows, so there is
   * nothing to scroll - and the difference only shows on a long one, where opening at the top
   * puts the current value hundreds of pixels out of sight. Time tracking's quarter-hour picker
   * is 96 items and opened on midnight every time, which is what prompted this.
   *
   * The container is scrolled directly rather than through scrollIntoView, which walks up to
   * every scrollable ancestor and would move the page behind this fixed-position menu.
   */
  useEffect(() => {
    if (!show) return;
    const menu = menuRef.current;
    const selected = menu?.querySelector('.selected-value') as HTMLElement | null;
    if (!menu || !selected) return;
    menu.scrollTop = selected.offsetTop - menu.clientHeight / 2 + selected.offsetHeight / 2;
  }, [show]);


  function parentData(data: any) {
    if (!data?.ParentId) {
      setActiveValueArray([]);
      if (data?.Value) setActiveValueArray([data?.Value]);
      return;
    }
    const parentCategoryValue = _.find(items, o => o?.id == data.ParentId);
    parentData(parentCategoryValue);
    setActiveValueArray(items_1 => [...items_1, data?.Value]);
  }

  function procesMultiNestedData(data: Item, dataId: number) {
    const key = getActiveValueKey(dataId);
    if (!data?.parentId) {
      setActiveValuesArray(items_1 => {
        const dataItems_1: string[] = items_1[key] ?? [];
        if (data?.value)
          dataItems_1.push(data.value);
        const newActiveValuesArray = { ...items_1, [key]: dataItems_1 };
        return newActiveValuesArray;
      });
      return;
    }
    const parentCategoryValue = _.find(items, o => o?.id == data.parentId);
    procesMultiNestedData(parentCategoryValue, dataId);
    setActiveValuesArray(items_1 => {
      const dataItems_1: string[] = items_1[key] ?? [];
      dataItems_1.push(data?.value);
      const newActiveValuesArray = { ...items_1, [key]: dataItems_1 };
      return newActiveValuesArray;
    });
  }

  useEffect(() => {
    if (isMulti) {
      const propValuesIds: string[] = defaultValue?.split(",") ?? [];
      const propValues = items
        .filter(p => propValuesIds.includes(p.id.toString()))
        .sort((a, b) => propValuesIds.indexOf(a.id.toString()) - propValuesIds.indexOf(b.id.toString()));
      let newActiveValuesIds = activeValuesIds ?? [];
      propValues.forEach(p => {
        if (!newActiveValuesIds.includes(p.id)) {
          newActiveValuesIds = [...newActiveValuesIds, p.id];
        }
        setActiveValuesIds(() => newActiveValuesIds);
        procesMultiNestedData(p, p.id);
      });
      if (triggerDefaultValueSet)
        onMultiChange?.(newActiveValuesIds);
    } else if (dataItem !== undefined) {
      setActiveValue(dataItem?.value)
      parentData(dataItem);
      if(triggerDefaultValueSet)
        onChange(items.find(item => item.id === dataItem.id ));
    } else {
      if (defaultValue) {
        setActiveValue(defaultValue)
        setActiveValueArray([defaultValue]);
        onChange(items.find(item => item.value === defaultValue));
      } else {
        setActiveValueArray([]);
      }
    }
  }, [dataItem]);


  useEffect(() => {
    function handleClickOutside(event: any) {
      const clickedInsideTrigger = wrapperRef.current?.contains(event.target);
      const clickedInsideMenu = menuRef.current?.contains(event.target);
      if (!clickedInsideTrigger && !clickedInsideMenu && show) {
        setShow(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [wrapperRef, menuRef, show]);

  const toTree = useCallback((data: Item[], pid = null) => {
    return data.sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0)).reduce((acc, item) => {
      if (item.parentId === pid) {
        const obj = { ...item };
        const children = toTree(data, item.id);
        if ((children?.length ?? -1) >= 0) obj['children'] = children;
        acc.push(obj);
      }
      return acc;
    }, []);
  },
    [items]
  );

  const getIconClass = () => {
    return disabled ? 'nested-dropdown-icon-disabled' : 'nested-dropdown-icon';
  }

  const getActiveValueKey = (id: number) => `activeValue_${id}`;

  const getForegroundColor = ({ siteVariables: { colorScheme } }: any) => {
    return disabled ? colorScheme.default.foregroundDisabled1 : colorScheme.default.foreground;
  }

  const onClickClear = (e: React.MouseEvent<SVGElement, MouseEvent>) => {
    e.stopPropagation();
    setActiveValueArray([]);
    onChange(null);
    setActiveValue('');
  }

  const onItemSelect = (item: any) => {
    if (isMulti) {
      const thisActiveValues = [...activeValuesIds, item.id];
      setActiveValuesIds(thisActiveValues);
      procesMultiNestedData(item, item.id);
      onMultiChange?.(thisActiveValues);
    } else {
      setActiveValue(item?.Value);
      parentData(item);
      onChange(item);
    }
    setShow(false);
  }

  const dropdownItems = useMemo(() => toTree(items), [items]);

  const flatItems = useMemo(() => flattenTree(dropdownItems), [dropdownItems]);

  const isItemDisabled = useCallback((item: any) => isMulti && activeValuesIds.some(av => av == item.id), [isMulti, activeValuesIds]);

  const firstEnabledIndex = useCallback(() => flatItems.findIndex(item => !isItemDisabled(item)), [flatItems, isItemDisabled]);

  const lastEnabledIndex = useCallback(() => {
    for (let i = flatItems.length - 1; i >= 0; i--) {
      if (!isItemDisabled(flatItems[i])) return i;
    }
    return -1;
  }, [flatItems, isItemDisabled]);

  const getInitialHighlightIndex = useCallback(() => {
    if (!isMulti && activeValue) {
      const idx = flatItems.findIndex(item => item.value === activeValue);
      if (idx >= 0 && !isItemDisabled(flatItems[idx])) return idx;
    }
    return firstEnabledIndex();
  }, [isMulti, activeValue, flatItems, isItemDisabled, firstEnabledIndex]);

  useEffect(() => {
    setHighlightedIndex(show ? getInitialHighlightIndex() : -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // If the currently-highlighted item becomes disabled (e.g. selected elsewhere) while the
  // menu stays open, nudge the highlight to the nearest still-enabled item instead of leaving
  // it stuck on an item Enter/Space can no longer select.
  useEffect(() => {
    if (!show) return;
    setHighlightedIndex(prev => {
      if (prev < 0 || prev >= flatItems.length || !isItemDisabled(flatItems[prev])) return prev;
      for (let offset = 1; offset < flatItems.length; offset++) {
        if (prev + offset < flatItems.length && !isItemDisabled(flatItems[prev + offset])) return prev + offset;
      }
      return firstEnabledIndex();
    });
  }, [show, flatItems, isItemDisabled, firstEnabledIndex]);

  const moveHighlight = useCallback((direction: 1 | -1) => {
    setHighlightedIndex(prev => {
      if (flatItems.length === 0) return -1;
      let next = prev;
      for (const _item of flatItems) {
        next = (next + direction + flatItems.length) % flatItems.length;
        if (!isItemDisabled(flatItems[next])) return next;
      }
      return prev;
    });
  }, [flatItems, isItemDisabled]);

  const selectHighlighted = useCallback(() => {
    const item = flatItems[highlightedIndex];
    if (item && !isItemDisabled(item)) onItemSelect(item);
  }, [flatItems, highlightedIndex, isItemDisabled, onItemSelect]);

  const handleTriggerKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        if (!show) {
          calculateMenuPosition();
          setShow(true);
        } else {
          moveHighlight(e.key === 'ArrowDown' ? 1 : -1);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        e.stopPropagation();
        if (!show) {
          calculateMenuPosition();
          setShow(true);
        } else {
          selectHighlighted();
        }
        break;
      case 'Home':
        if (show) {
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex(firstEnabledIndex());
        }
        break;
      case 'End':
        if (show) {
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex(lastEnabledIndex());
        }
        break;
      case 'Escape':
        if (show) {
          e.preventDefault();
          e.stopPropagation();
          setShow(false);
        }
        break;
      default:
        break;
    }
  }, [disabled, show, calculateMenuPosition, moveHighlight, selectHighlighted, firstEnabledIndex, lastEnabledIndex]);

  const highlightedItemId = flatItems[highlightedIndex]?.id;
  const activeDescendant = show && highlightedItemId != null ? getOptionId(highlightedItemId) : undefined;

  useEffect(() => {
    if (!show || highlightedItemId == null) return;
    document.getElementById(getOptionId(highlightedItemId))?.scrollIntoView({ block: 'nearest' });
  }, [show, highlightedItemId, getOptionId]);

  const renderMultiItems = () => {
    const nodeItems = Object.keys(activeValuesArray).map(k => {
      return <Pill
        key={k}
        size={"small"}
        styles={{ minWidth: "unset" }}
        actionable
        id={k}
        appearance="inverted"
        onDismiss={(e, data) => {
          const id = Number(k.replace(/^activeValue_/, ''));
          const thisActiveValues = activeValuesIds.filter(v => v !== id);
          setActiveValuesIds(av => av.filter(v => v !== id));
          setActiveValuesArray(items_1 => {
            const newState = { ...items_1 };
            delete newState[k];
            return newState;
          });
          onMultiChange?.(thisActiveValues);
        }}
        content={{
          content: activeValuesArray[k].join(' > '),
          styles: { fontSize: "0.875rem !important", fontWeight: 600 }
        }}
      />;
    });
    return nodeItems;
  }

  // Extracted from an inline nested ternary so the trigger's accessible-name attribute is easy to follow.
  let labelProps: Record<string, string> = {};
  if (label) {
    labelProps = { 'aria-labelledby': `${instanceIdRef.current}-label` };
  } else if (placeholder) {
    labelProps = { 'aria-label': placeholder };
  }

  const triggerTabIndex = disabled ? -1 : (tabIndex ?? 0);

  return (
    <ProviderConsumer
      render={globalTheme => {
        return (
          <div ref={wrapperRef} className={`relative rounded ${error && 'border border-red-700'}`} style={{ pointerEvents: disabled ? 'none' : 'auto', ...ThemeColorScheme(globalTheme.siteVariables) }}>
            {(label && !isRequired) && <div id={`${instanceIdRef.current}-label`}>{label}</div>}
            {(label && isRequired) && <Flex>
              <span id={`${instanceIdRef.current}-label`}>
                <Text content={label} style={{ display: 'block' }} />
              </span>
              <span style={{ color: 'red', marginLeft: 2 }}>*</span>
            </Flex>}
            <div ref={triggerRef} tabIndex={triggerTabIndex}
              className={`w-full flex items-center justify-between rounded cursor-pointer ${Styles.Container} ${isMulti ? Styles.MultiContainer : ""}`}
              style={{ backgroundColor: getBackgroundColor(globalTheme, disabled) }}
              onClick={handleToggle}
              onKeyDown={handleTriggerKeyDown}
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={show}
              aria-controls={listboxId}
              aria-owns={listboxId}
              aria-disabled={disabled}
              {...labelProps}
              {...(activeDescendant && { 'aria-activedescendant': activeDescendant })}
            >
              {!isMulti
                ? (
                  <span dir="rtl" className={`text-sm ${Styles.value} w-full truncate mr-4`} style={{ color: getForegroundColor(globalTheme) }}>
                    <bdi>{activeValueArray?.length > 0 ? activeValueArray.join(' > ') : placeholder}</bdi>
                  </span>
                )
                : (<Flex wrap>
                  {renderMultiItems()}
                </Flex>)
              }
              {showClearBtn && !disabled && activeValueArray.length > 0 ?
                <CloseIcon className={`${getIconClass()}`} onClick={e => onClickClear(e)} /> :
                <DownArrow className={`${getIconClass()}`} />}
            </div>
            {show && ReactDOM.createPortal(
              <div ref={menuRef} id={listboxId} role="listbox" style={{
                boxShadow: `${getDropdownBoxShadow(globalTheme)}`, position: 'fixed', top: menuPosition.top, left: menuPosition.left, width: menuPosition.width, zIndex: 9999999999999,
                ...ThemeColorScheme(globalTheme.siteVariables), maxHeight: DROPDOWN_MAX_HEIGHT,
                ...(showAbove && { top: 'unset', bottom: `calc(100vh - ${menuPosition.top + DROPDOWN_MAX_HEIGHT}px)` })
              }}
                className={`${Styles.OptionContainer} rounded overflow-auto`}>
                {dropdownItems.length <= 0 ? <>
                  <div className="h-full flex flex-col items-center justify-center py-2">
                    <span>No data found</span>
                  </div>
                </> : <>
                  <RecursiveTree
                    data={dropdownItems}
                    activeValue={activeValue}
                    onSelectCallback={(item: any) => onItemSelect(item)}
                    isMulti={isMulti}
                    activeValueIds={activeValuesIds}
                    highlightedItemId={highlightedItemId}
                    getOptionId={getOptionId}
                  />
                </>}
              </div>, document.body)}
          </div>
        );
      }}
    />
  );
};
